import {ComponentFactory, ComponentFactoryResolver, Inject, Injectable, Injector, Optional} from '@angular/core';
import {NgxFilterConfig} from "./ngx-filter-config";
import {DateComponent} from "./components/date/date.component";
import {NumberBoxComponent} from "./components/number-box/number-box.component";
import {TextBoxComponent} from "./components/text-box/text-box.component";
import {TrueFalseComponent} from "./components/true-false/true-false.component";
import {MultiSelectBoxComponent} from "./components/select/multi-select-box/multi-select-box.component";
import {SelectBoxComponent} from "./components/select/select-box/select-box.component";
import * as moment from "jalali-moment";
import {ConditionsSelectorComponent} from "./components/conditions-selector/conditions-selector.component";
import {ConditionOperator, Filter, FilterTranslator, FilterTypes, PageFilter} from "./ngx-types";
import {NGX_FILTERS_CONFIGS} from "./ngx-filters-token";


@Injectable({
  providedIn: 'root'
})
export class NgxFiltersService {
  translator: FilterTranslator = {} as any
  filtersComponent: {
    textBox?: ComponentFactory<any>,
    numberBox?: ComponentFactory<any>,
    date?: ComponentFactory<any>,
    selectBox?: ComponentFactory<any>,
    multiSelectBox?: ComponentFactory<any>,
    trueFalse?: ComponentFactory<any>,
    conditionSelector?: ComponentFactory<any>
  } = {};
  private _configs: NgxFilterConfig;

  constructor(
    injector: Injector,
    @Optional() @Inject(NGX_FILTERS_CONFIGS) configs: NgxFilterConfig,
    private resolver: ComponentFactoryResolver,
  ) {
    const defaultConfigs = {
      trueFalseComponent: TrueFalseComponent,
      dateComponent: DateComponent,
      multiSelectBoxComponent: MultiSelectBoxComponent,
      selectBoxComponent: SelectBoxComponent,
      numberBoxComponent: NumberBoxComponent,
      textBoxComponent: TextBoxComponent,
      conditionSelectorComponent: ConditionsSelectorComponent,
      translator: new FilterTranslator(),
    }
    if (!configs) {
      configs = defaultConfigs;
    } else {
      configs = Object.assign(defaultConfigs, configs);
    }
    this._configs = configs;
    this.filtersComponent.textBox = this.resolver.resolveComponentFactory(configs.textBoxComponent);
    this.filtersComponent.numberBox = this.resolver
      .resolveComponentFactory(configs.numberBoxComponent);
    this.filtersComponent.date = this.resolver
      .resolveComponentFactory(configs.dateComponent);
    this.filtersComponent.selectBox = this.resolver.resolveComponentFactory(configs.selectBoxComponent);
    this.filtersComponent.multiSelectBox = this.resolver.resolveComponentFactory(configs.multiSelectBoxComponent);
    this.filtersComponent.trueFalse = this.resolver.resolveComponentFactory(configs.trueFalseComponent);
    this.filtersComponent.conditionSelector = this.resolver.resolveComponentFactory(configs.conditionSelectorComponent);
    this.translator = configs.translator;
  }

  getComponentFactory<T>(filterType: FilterTypes): ComponentFactory<T> {
    let component: any = null;
    switch (filterType) {
      case FilterTypes.DateTime:
        component = this._configs.dateComponent;
        break;
      //case FilterTypes.ListCheck:
      case FilterTypes.MultiSelect:
        component = this._configs.multiSelectBoxComponent
        break;
      case FilterTypes.Number:
        component = this._configs.numberBoxComponent;
        break;
      case FilterTypes.SingleSelect:
        component = this._configs.selectBoxComponent;
        break;
      case FilterTypes.TextBox:
        component = this._configs.textBoxComponent;
        break;
      case FilterTypes.TrueFalse:
        component = this._configs.trueFalseComponent;
        break;

      default: {
        component = this._configs.conditionSelectorComponent;

      }
    }
    if (component)
      return this.resolver
        .resolveComponentFactory<T>(component);
    return null;
  }

  conditionSource: any[] = [];

  public useJalaliDate = false;

  checkHasFilter(filter: Filter): boolean {
    let hasFilter = (this.noValueConditions.includes(filter.conditionOperator))
      ||
      (filter.type == FilterTypes.MultiSelect ? filter.values && filter.values.length > 0 : !!filter.value);
    if (!hasFilter) {
      if (filter.type === FilterTypes.Number && filter.value == 0) {
        hasFilter = true;
      }
      if (filter.type === FilterTypes.Number && filter.conditionOperator === ConditionOperator.Between && filter.valueTo) {
        filter.value = 0;
        hasFilter = true;
      }
    }
    return (hasFilter);

  }

  public getConditionSource() {
    const conditions: any[] = [];
    for (let enumMember in ConditionOperator) {
      const condition = ConditionOperator[enumMember];
      const gcplat = this.getCondition(condition, null);
      if (gcplat) {
        conditions.push({
          key: ConditionOperator[enumMember],
          label: gcplat.label,
          types: gcplat.types
        });
      }
    }
    return conditions;
  }

  getDefaultCondition(filterType: FilterTypes): ConditionOperator {
    switch (filterType) {
      case FilterTypes.MultiSelect:
      case FilterTypes.ListCheck: {
        return ConditionOperator.InRange;
      }
      case FilterTypes.TrueFalse:
      case FilterTypes.SingleSelect:
      case FilterTypes.Number: {
        return ConditionOperator.Equal;
      }
      case FilterTypes.TextBox: {
        return ConditionOperator.Contains;
      }
      case FilterTypes.DateTime: {
        return ConditionOperator.Between;
      }
    }
  }

  private getConditionLabel(condition: ConditionOperator): string {
    if (condition) {
      const conditionKey = condition[0].toLowerCase() + condition.substring(1);
      if (this.translator) {
        return this.translator[conditionKey + 'Label'];
      } else {
        return condition;
      }
    }
    return "";

  }


  public getCondition(condition: ConditionOperator, filter: Filter): { label: string, types: FilterTypes[], hasValue: boolean } {
    condition = condition || filter.conditionOperator;
    const label = this.getConditionLabel(condition);
    switch (condition) {
      case ConditionOperator.Between:
        return {label, types: [FilterTypes.Number, FilterTypes.DateTime], hasValue: true};
      case ConditionOperator.Contains:
        return {label, types: [FilterTypes.TextBox], hasValue: true};
      case ConditionOperator.EndWith:
        return {label, types: [FilterTypes.TextBox], hasValue: true};
      case ConditionOperator.NotContains:
        return {label, types: [FilterTypes.TextBox], hasValue: true};
      case ConditionOperator.Equal:
        return {label, types: [FilterTypes.Number], hasValue: true};
      case ConditionOperator.Exact:
        return {label, types: [FilterTypes.DateTime, FilterTypes.TextBox], hasValue: true};
      case ConditionOperator.GreaterThan:
        return {label, types: [FilterTypes.Number, FilterTypes.DateTime], hasValue: true};
      case ConditionOperator.GreaterThanOrEqual:
        return {label, types: [FilterTypes.Number, FilterTypes.DateTime], hasValue: true};
      case ConditionOperator.LessThan:
        return {label, types: [FilterTypes.Number, FilterTypes.DateTime], hasValue: true};
      case ConditionOperator.LessThanOrEqual:
        return {label, types: [FilterTypes.Number, FilterTypes.DateTime], hasValue: true};
      case ConditionOperator.NotEqual:
        return {label, types: [FilterTypes.Number], hasValue: true};
      case ConditionOperator.StartWith:
        return {label, types: [FilterTypes.TextBox], hasValue: true};
      case ConditionOperator.InRange:
        return {label, types: [FilterTypes.ListCheck], hasValue: true};
      case ConditionOperator.LastMonth:
        return {label, types: [FilterTypes.DateTime], hasValue: false};
      case ConditionOperator.LastWeek:
        return {label, types: [FilterTypes.DateTime], hasValue: false};
      case ConditionOperator.CurrentMonth:
        return {label, types: [FilterTypes.DateTime], hasValue: false};
      case ConditionOperator.CurrentWeek:
        return {label, types: [FilterTypes.DateTime], hasValue: false};
      case ConditionOperator.CurrentYear:
        return {label, types: [FilterTypes.DateTime], hasValue: false};
      case ConditionOperator.ToDay:
        return {label, types: [FilterTypes.DateTime], hasValue: false};

    }
  }

  get noValueConditions(): ConditionOperator[] {
    return [
      ConditionOperator.LastWeek,
      ConditionOperator.LastMonth,
      ConditionOperator.CurrentYear,
      ConditionOperator.CurrentWeek,
      ConditionOperator.CurrentMonth,
      ConditionOperator.ToDay
    ];
  }

  getTime(dt): string {
    if (!dt) {
      return '';
    }
    const date = new Date(dt);
    const day = date.getDate();       // yields date
    const month = date.getMonth() + 1;    // yields month (add one as '.getMonth()' is zero indexed)
    const year = date.getFullYear();  // yields year
    const hour = date.getHours();     // yields hours
    const minute = date.getMinutes(); // yields minutes
    const second = date.getSeconds(); // yields seconds

// After this construct a string with the above results as below
    return day + '/' + month + '/' + year + " " + hour + ':' + minute + ':' + second;

  }

  setValueForDateHasNoValue(filter: Filter): void {
    if (filter.type === FilterTypes.DateTime) {
      const date = new Date();
      let fromDate = filter.value;
      let toDate = filter.valueTo;
      switch (filter.conditionOperator) {
        case ConditionOperator.LastWeek: {
          fromDate = date.setTime(date.getTime() - 6 * 24 * 60 * 60 * 1000);
          toDate = date.setTime(date.getTime() - (date.getDay() ? date.getDay() : 7) * 24 * 60 * 60 * 1000);
        }
          break;
        case ConditionOperator.LastMonth: {
          fromDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
          toDate = new Date(date.getFullYear(), date.getMonth(), 0);
        }
          break;
        case ConditionOperator.CurrentWeek: {
          const first = date.getDate() - date.getDay(); // First day is the day of the month - the day of the week
          const last = first + 6; // last day is the first day + 6

          fromDate = new Date(date.setDate(first)).toUTCString();
          toDate = new Date(date.setDate(last)).toUTCString();
        }
          break;
        case ConditionOperator.CurrentMonth: {
          const y = date.getFullYear(), m = date.getMonth();
          fromDate = new Date(y, m, 1);
          toDate = new Date(y, m + 1, 0);
        }
          break;
        case ConditionOperator.CurrentYear: {
          fromDate = new Date(date.getFullYear(), 0, 1);
          toDate = new Date(date.getFullYear() + 1, 0, 0);
        }
          break;
        case ConditionOperator.ToDay: {
          fromDate = date.setHours(0, 0, 0, 0);

          const end = new Date();
          end.setHours(23, 59, 59, 999);
          toDate = end;
        }
          break;

      }
      filter.value = fromDate;
      filter.valueTo = toDate;
    }
  }

  clearFilter(filter: Filter): void {
    filter.hasFilter = false;
    if (filter.type === FilterTypes.DateTime) {
      filter.conditionOperator = this.getDefaultCondition(filter.type);
    }
    switch (filter.type) {
      case FilterTypes.DateTime:
      case FilterTypes.Number:
      case FilterTypes.TextBox:
      case FilterTypes.SingleSelect:
      case FilterTypes.TrueFalse: {
        filter.value = null;
        filter.valueTo = null;
      }
        break;
      case FilterTypes.MultiSelect:
      case FilterTypes.ListCheck: {
        filter.values = null;
      }

    }
  }

  getDisplayLabel(filter: Filter, hasConditionName: boolean = true): string {

    let condition = this.getCondition(null, filter);
    if (!condition) {
      return ' ';
    }

    filter.conditionName = condition.label;
    if (this.noValueConditions.includes(filter.conditionOperator)) {
      return filter.conditionName;
    }

    let conditionName = hasConditionName ? filter.conditionName : '';

    if (!condition.hasValue) {
      return conditionName;
    }

    if (filter.type == FilterTypes.MultiSelect) {
      if (!filter.values) {
        return '';
      }
      return filter.values.map(z => z[filter.options?.selectBoxOptions?.displayMember]).join(' ,');
    } else if (filter.type == FilterTypes.SingleSelect) {
      if (!filter.value) {
        return '';
      }
      return filter.value[filter.options?.selectBoxOptions?.displayMember];
    } else {
      if (!filter.value) {
        return '';
      }
      if (filter.type == FilterTypes.TrueFalse) {
        if (filter.value == true) {
          filter.valueName = filter?.options?.trueFalseOptions?.trueLabel ? filter.options.trueFalseOptions.trueLabel : 'Yes';
        } else {
          filter.valueName = filter?.options?.trueFalseOptions?.falseLabel ? filter.options.trueFalseOptions.falseLabel : 'No';
        }
        return `${filter.valueName}`;
      }
      if (filter.type == FilterTypes.DateTime) {
        if (!this.useJalaliDate) {
          filter.valueName = moment(filter.value).format('YYYY/MM/DD');
        } else {
          filter.valueName = moment(filter.value).format('jYYYY/jMM/jDD');
        }
        if (filter.conditionOperator == ConditionOperator.Between) {
          conditionName = '';
          const fromLabel = (this.translator.fromLabel) ? this.translator.fromLabel : 'from';
          const toLabel = (this.translator?.toLabel) ? this.translator.toLabel : 'to';
          if (filter.valueTo) {
            if (!this.useJalaliDate) {
              filter.valueToName = moment(filter.valueTo).format('YYYY/MM/DD');
            } else {
              filter.valueToName = moment(filter.valueTo).format('jYYYY/jMM/jDD');
            }
          } else {
            filter.valueToName = '...';
          }
          // filter.valueName = moment(filter.value).format('jYYYY/jMM/jDD');
          return `${conditionName} ${fromLabel} ${filter.valueName} ${toLabel} ${filter.valueToName}`;
        }
        return `${conditionName} ${filter.valueName}`;
      } else {
        let prefix = filter.options?.numberBoxOptions?.prefixTextForHint;
        prefix = prefix ? prefix : '';
        if (!prefix && filter.options?.numberBoxOptions?.isMoney) {
          prefix = '$';
        }
        if (filter.options?.numberBoxOptions?.isMoney && filter.type == FilterTypes.Number) {
          filter.valueName = this._money(filter.value);
          filter.valueTo ? filter.valueToName = this._money(filter.valueTo) : '...';
        } else {
          filter.valueName = filter.value;
          filter.valueToName = filter.valueTo;
        }
        if (filter.conditionOperator == ConditionOperator.Between) {
          conditionName = '';
          const fromLabel = (this.translator?.fromLabel) ? this.translator.fromLabel : 'from';
          const toLabel = (this.translator?.toLabel) ? this.translator.toLabel : 'to';

          return `${conditionName} ${fromLabel} ${filter.valueName}${prefix} ${toLabel} ${filter.valueToName ? filter.valueToName : '...'}${prefix}`;
        } else {
          return `${conditionName} ${filter.valueName}${prefix}`;
        }
      }
    }
  }

  private toQueryString(queryObject: PageFilter): string {
    const parts = [];
    Object.keys(queryObject).forEach((property) => {
      const value = queryObject[property];
      if (value !== undefined && value !== undefined) {
        parts.push(
          encodeURIComponent(property) + '=' + encodeURIComponent(value)
        );
      }
    });
    return parts.join('&');
  }

  getFiltersInQueryString(filterBy: Filter[]): string {
    let obj = '';
    filterBy.forEach((f, i) => {
      Object.keys(f).forEach((prop) => {
        const value = filterBy[i][prop];
        if (Array.isArray(value)) {
          const values = value as string[];
          let bb = 0;
          values.forEach((b) => {
            if (b !== undefined && b !== null) {
              obj =
                obj +
                '&' +
                encodeURIComponent('filters') +
                `[${i}]` +
                '.' +
                `${encodeURIComponent(prop)}[${bb}]` +
                '=' +
                encodeURIComponent(b);
              bb++;
            }
          });
        } else if (value !== undefined && value !== null) {
          obj =
            obj +
            '&' +
            encodeURIComponent('filters') +
            `[${i}]` +
            '.' +
            encodeURIComponent(prop) +
            '=' +
            encodeURIComponent(value);
        }
      });
    });
    return obj;
  }

  getPageFilterInQueryString(queryObject: PageFilter): string {
    let obj: string;
    if (queryObject && queryObject.filters) {
      const filterBy = queryObject.filters.slice();
      const query = new PageFilter();
      Object.keys(queryObject).forEach((o) => {
        // copy(slice) from queryObject
        query[o] = queryObject[o];
      });
      delete query.filters;
      obj = this.toQueryString(query);
      obj += this.getFiltersInQueryString(filterBy);
    }
    return obj;
  }

  private _numberWithCommas(x) {
    return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? '';
  }

  private _money(value: any): string {

    if (value != null) {
      return this._numberWithCommas(value);
    }
    return '0';
  }
}
